import { SidebarMenuButton } from '@/components/ui/sidebar'
import { Palette } from 'lucide-react'
import { useSidebar } from '@/components/ui/sidebar'
import { useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '@redux/hooks/useAppState'
import { setColorIndex } from '@redux/store/slices/theme/themeSlice'

// Brighter preset colors inspired by HoneyGrid gradient
export const presetColors = [
	{ 
		name: 'HoneyGrid Purple', 
		primary: { l: 0.5944, c: 0.196, h: 298.47 },
		oklch: 'oklch(0.5944 0.196 298.47)',
		hex: '#905BE1'
	},
	{ 
		name: 'Ocean Teal', 
		primary: { l: 0.68, c: 0.11, h: 185 },
		oklch: 'oklch(68% 0.11 185)',
		hex: '#2BA5B0'
	},
	{ 
		name: 'Forest Green', 
		primary: { l: 0.65, c: 0.13, h: 135 },
		oklch: 'oklch(65% 0.13 135)',
		hex: '#4A9B5E'
	},
	{ 
		name: 'Golden Sun', 
		primary: { l: 0.78, c: 0.12, h: 85 },
		oklch: 'oklch(78% 0.12 85)',
		hex: '#D4B942'
	},
	{ 
		name: 'Sunset Orange', 
		primary: { l: 0.72, c: 0.15, h: 45 },
		oklch: 'oklch(72% 0.15 45)',
		hex: '#FF8C42'
	},
	{ 
		name: 'Cherry Blossom', 
		primary: { l: 0.68, c: 0.18, h: 15 },
		oklch: 'oklch(68% 0.18 15)',
		hex: '#FF6B9D'
	},
]

export const ColorToggle = () => {
	const { state } = useSidebar()
	const dispatch = useAppDispatch()
	const currentColorIndex = useAppSelector((state) => state.theme.colorIndex)
	const perfectSquareClass = "h-10 w-full px-3 group-data-[collapsible=icon]:!w-10 group-data-[collapsible=icon]:!h-10 group-data-[collapsible=icon]:!p-3 group-data-[collapsible=icon]:justify-center"

	// Apply color to the entire app
	useEffect(() => {
		const root = document.documentElement
		const color = presetColors[currentColorIndex].primary
		
		// Update primary color
		root.style.setProperty('--primary', `oklch(${color.l} ${color.c} ${color.h})`)
		root.style.setProperty('--accent', `oklch(${color.l} ${color.c} ${color.h})`)
		
		// Update secondary to be a lighter version of primary
		root.style.setProperty('--secondary', `oklch(${Math.min(color.l + 0.15, 0.95)} ${color.c * 0.6} ${color.h})`)
		
		// Update ring color
		root.style.setProperty('--ring', `oklch(${color.l} ${color.c} ${color.h})`)
		
		// Update sidebar colors
		root.style.setProperty('--sidebar-primary', `oklch(${color.l} ${color.c} ${color.h})`)
		root.style.setProperty('--sidebar-accent', `oklch(${color.l} ${color.c} ${color.h})`)
		root.style.setProperty('--sidebar-ring', `oklch(${color.l} ${color.c} ${color.h})`)
		
		// Update component colors for dynamic manipulation
		root.style.setProperty('--primary-action-l', `${color.l * 100}%`)
		root.style.setProperty('--primary-action-c', color.c.toString())
		root.style.setProperty('--primary-action-h', color.h.toString())
	}, [currentColorIndex])

	const handleColorToggle = () => {
		const nextIndex = (currentColorIndex + 1) % presetColors.length
		dispatch(setColorIndex(nextIndex))
	}

	const currentColor = presetColors[currentColorIndex]

	return (
		<SidebarMenuButton
			onClick={handleColorToggle}
			className={`${perfectSquareClass} justify-start gap-2 cursor-pointer text-base`}
			tooltip={`Color: ${currentColor.name}`}
		>
			<div className="relative size-4">
				<Palette className="size-4" />
				<div 
					className="absolute -bottom-0.5 -right-0.5 size-1.5 rounded-full border border-background"
					style={{ backgroundColor: currentColor.oklch }}
				/>
			</div>
			{state === 'expanded' && <span>{currentColor.name}</span>}
		</SidebarMenuButton>
	)
}